// dialogs/ResourceDialog/view.js - Honey-themed ResourceDialog view
import React from 'react';
import {
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormHelperText,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  MeetingRoom as MeetingRoomIcon,
  Add as AddIcon
} from '@mui/icons-material';

// Import honey-themed styled components
import {
  HoneyDialog,
  HoneyDialogTitle,
  HoneyCloseButton,
  HoneyDialogContent,
  HoneyModalForm,
  HoneyModalTextField,
  HoneyModalFormControl,
  HoneyDialogActions,
  HoneyCreateButton,
  HoneyCancelButton,
  HoneyModalSubtitle,
  HoneyModalRequired,
  HoneyModalDivider
} from './styled';

const ResourceDialogView = ({
  // Dialog state
  open,
  onClose,
  isEditing,
  isSubmitting,
  
  // Form data
  currentResource,
  workAreas,
  resourceTypes,
  
  // Form validation
  errors,
  isFormValid,
  
  // Handlers
  handleInputChange,
  handleSubmit,
  handleClose
}) => {
  return (
    <HoneyDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <HoneyDialogTitle>
        <MeetingRoomIcon sx={{ fontSize: '1.8rem' }} />
        {isEditing ? 'Edit Meeting Room' : 'Create New Meeting Room'}
        
        <HoneyCloseButton onClick={handleClose} disabled={isSubmitting}>
          <CloseIcon />
        </HoneyCloseButton>
      </HoneyDialogTitle>

      <HoneyDialogContent>
        <HoneyModalSubtitle>
          {isEditing 
            ? 'Update the details of your meeting room'
            : 'Add a new meeting room to your venue with custom capacity and location'
          }
        </HoneyModalSubtitle>

        <HoneyModalForm>
          <Grid container spacing={3}>
            {/* Meeting Room Name */}
            <Grid item xs={12}>
              <HoneyModalTextField
                fullWidth
                label={
                  <>
                    Meeting Room Name <HoneyModalRequired>*</HoneyModalRequired>
                  </>
                }
                name="name"
                value={currentResource?.name || ''}
                onChange={handleInputChange}
                error={!!errors.name}
                helperText={errors.name || 'Enter a descriptive name for your meeting room'}
                placeholder="e.g., Conference Room A, Executive Suite..."
                disabled={isSubmitting}
                autoFocus
                required
              />
            </Grid>

            {/* Work Area */}
            <Grid item xs={12}>
              <HoneyModalFormControl fullWidth error={!!errors.work_area} required>
                <InputLabel>
                  Work Area <HoneyModalRequired>*</HoneyModalRequired>
                </InputLabel>
                <Select
                  name="work_area"
                  value={currentResource?.work_area || ''}
                  onChange={handleInputChange}
                  label="Work Area *"
                  disabled={isSubmitting}
                >
                  {workAreas?.map((area) => (
                    <MenuItem key={area.id} value={area.id}>
                      {area.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {errors.work_area || 'Choose the location for this meeting room'}
                </FormHelperText>
              </HoneyModalFormControl>
            </Grid>

            {/* Capacity */}
            <Grid item xs={12} sm={6}>
              <HoneyModalTextField
                fullWidth
                label={
                  <>
                    Capacity <HoneyModalRequired>*</HoneyModalRequired>
                  </>
                }
                name="capacity"
                type="number"
                value={currentResource?.capacity || 4}
                onChange={handleInputChange}
                error={!!errors.capacity}
                helperText={errors.capacity || 'Maximum number of people'}
                inputProps={{ 
                  min: 1, 
                  max: 50,
                  step: 1
                }}
                placeholder="4"
                disabled={isSubmitting}
                required
              />
            </Grid>

            {/* Status - Disabled for new rooms */}
            <Grid item xs={12} sm={6}>
              <Tooltip title="New meeting rooms are created as inactive and must meet requirements before activation">
                <HoneyModalFormControl fullWidth disabled>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value="IN"
                    label="Status"
                  >
                    <MenuItem value="IN">Inactive</MenuItem>
                  </Select>
                  <FormHelperText>
                    New meeting rooms are created as inactive by default. You can activate them later after adding photos, description, and pricing.
                  </FormHelperText>
                </HoneyModalFormControl>
              </Tooltip>
            </Grid>
          </Grid>

          <HoneyModalDivider />

          {/* Info text */}
          <HoneyModalSubtitle sx={{ mb: 0, fontSize: '0.85rem', opacity: 0.8 }}>
            💡 You can add services, images, and pricing after creating the meeting room
          </HoneyModalSubtitle>
        </HoneyModalForm>
      </HoneyDialogContent>

      <HoneyDialogActions>
        <HoneyCancelButton 
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </HoneyCancelButton>
        
        <HoneyCreateButton
          onClick={handleSubmit}
          disabled={isSubmitting || !isFormValid}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={20} sx={{ color: 'currentColor' }} />
            ) : (
              <AddIcon />
            )
          }
        >
          {isSubmitting 
            ? 'Creating...' 
            : isEditing 
              ? 'Update Room' 
              : 'Add Meeting Room'
          }
        </HoneyCreateButton>
      </HoneyDialogActions>
    </HoneyDialog>
  );
};

export default ResourceDialogView;